<template>
	<section class="section-card chart-card" v-loading="loading">
		<div class="section-head section-head--compact">
			<h2 class="section-title">压力趋势曲线</h2>
			<el-date-picker
				v-model="selectDate"
				class="time-range-picker"
				type="date"
				value-format="yyyy-MM-dd"
				placeholder="选择日期"
				:clearable="false"
			></el-date-picker>
		</div>

		<div class="chart-shell">
			<div class="chart-legend">
				<div v-for="item in chartData.legendItems" :key="item.name" class="chart-legend__item">
					<span class="chart-legend__dot" :style="{ background: item.color }"></span>
					<span>{{ item.name }}</span>
				</div>
			</div>

			<div ref="chartRef" class="chart-canvas"></div>
		</div>
	</section>
</template>

<script>
const echarts = require('echarts/lib/echarts')
import { apiGetPressureRegulatorPressureRecords } from '@/apis/pressureRegulatorManage.api'
require('echarts/lib/chart/line')
require('echarts/lib/component/grid')
require('echarts/lib/component/tooltip')
require('echarts/lib/component/markLine')
require('echarts/lib/component/title')

export default {
	name: 'PressureTrendChart',
	props: {
		collectorDeviceNo: {
			type: [String, Number],
			default: '',
		},
	},
	data() {
		return {
			selectDate: this.dayjs().subtract(1, 'day').format('YYYY-MM-DD'),
			loading: false,
			chartInstance: null,
			chartData: {
				axisTimes: [],
				legendItems: [],
				series: [],
			},
		}
	},
	watch: {
		collectorDeviceNo: {
			handler(value) {
				if (value) {
					this.getPressureRecords()
				}
			},
			immediate: true,
		},
		selectDate() {
			this.getPressureRecords()
		},
	},
	mounted() {
		this.$nextTick(() => {
			this.initChart()
			this.getPressureRecords()
			window.addEventListener('resize', this.handleResize)
		})
	},
	beforeDestroy() {
		window.removeEventListener('resize', this.handleResize)
		if (this.chartInstance) {
			this.chartInstance.dispose()
			this.chartInstance = null
		}
	},
	methods: {
		initChart() {
			if (!this.$refs.chartRef) {
				return
			}

			if (!this.chartInstance) {
				this.chartInstance = echarts.init(this.$refs.chartRef, null, { renderer: 'canvas' })
			}

			this.renderChart()
		},
		handleResize() {
			if (this.chartInstance) {
				this.chartInstance.resize()
			}
		},
		async getPressureRecords() {
			if (!this.collectorDeviceNo) {
				this.setChartData([])
				return
			}
			this.loading = true
			try {
				const records = await apiGetPressureRegulatorPressureRecords({
					collectorDeviceNo: this.collectorDeviceNo,
					sampleType: 'HOUR',
					...this.getTimeRange(),
				})
				this.setChartData(records || [])
				this.$nextTick(() => {
					this.handleResize()
				})
			} finally {
				this.loading = false
			}
		},
		getTimeRange() {
			return {
				startTime: this.selectDate ? `${this.selectDate} 00:00:00` : undefined,
				endTime: this.selectDate ? `${this.selectDate} 23:59:59` : undefined,
			}
		},
		setChartData(devices) {
			const colorList = ['#2563eb', '#f97316', '#16a34a', '#8b5cf6', '#06b6d4', '#e11d48', '#f59e0b']
			const timeSet = new Set()
			const normalizedDevices = devices.map((device, index) => {
				const pressureRecords = Array.isArray(device.pressureRecords) ? device.pressureRecords : []
				const pressureMap = new Map()
				pressureRecords.forEach(item => {
					const timeKey = this.formatRecordTime(item.dataTime)
					if (!timeKey) return
					timeSet.add(timeKey)
					pressureMap.set(timeKey, this.toNumberOrNull(item.pressure))
				})
				return {
					...device,
					name: `CH${device.subDeviceNo ?? index + 1}`,
					color: colorList[index % colorList.length],
					pressureMap,
					thresholdInfo: this.getThresholdInfo(device.dynamicBaseAttributes || []),
				}
			})
			const axisTimes = Array.from(timeSet).sort(
				(left, right) => this.dayjs(left).valueOf() - this.dayjs(right).valueOf(),
			)
			const series = normalizedDevices.map(device => {
				const values = axisTimes.map(timeKey => {
					const value = device.pressureMap.get(timeKey)
					return value === undefined ? null : value
				})
				return {
					name: device.name,
					type: 'line',
					smooth: true,
					symbol: 'circle',
					symbolSize: 6,
					data: values,
					lineStyle: {
						width: 1.5,
						color: device.color,
					},
					itemStyle: {
						color: device.color,
						borderColor: '#ffffff',
						borderWidth: 2,
					},
				}
			})
			const values = normalizedDevices
				.flatMap(device => Array.from(device.pressureMap.values()))
				.filter(item => item !== null)
			const maxValue = values.length ? Math.max(...values) : 2

			this.chartData = {
				...this.chartData,
				max: Math.ceil(Math.max(2, maxValue) * 10) / 10,
				axisTimes,
				legendItems: normalizedDevices.map(device => ({
					name: device.name,
					color: device.color,
					thresholdText: device.thresholdInfo.text,
					installationPosition: device.thresholdInfo.positionText,
				})),
				series,
			}
			this.renderChart()
		},
		toNumberOrNull(value) {
			if (value === null || value === undefined || value === '') return null
			const numberValue = Number(value)
			return Number.isNaN(numberValue) ? null : numberValue
		},
		formatRecordTime(value) {
			if (!value) return ''
			return this.dayjs(value).format('YYYY-MM-DD HH:mm:ss')
		},
		getThresholdInfo(attributes) {
			const list = Array.isArray(attributes) ? attributes : []
			const getAttr = key => list.find(item => item.attributeKey === key) || {}
			const formatValue = value => (value === null || value === undefined || value === '' ? '--' : value)
			const positionAttr = getAttr('installationPosition')
			const positionValue = positionAttr.value ?? positionAttr.defaultValue
			const positionText = this.resolveTranslation(positionAttr.interactiveTranslationDes, positionValue)
			const upperAttr = getAttr('normalUpperLimit')
			const lowerAttr = getAttr('normalLowerLimit')
			const upperLimit = formatValue(upperAttr.value ?? upperAttr.defaultValue)
			const lowerLimit = formatValue(lowerAttr.value ?? lowerAttr.defaultValue)
			const text = positionText
				? `${positionText}阈值：上限 ${upperLimit} / 下限 ${lowerLimit}`
				: `阈值：上限 ${upperLimit} / 下限 ${lowerLimit}`
			return {
				positionValue,
				positionText,
				upperLimit,
				lowerLimit,
				text,
			}
		},
		resolveTranslation(translations, value) {
			if (!translations || value === null || value === undefined || value === '') return ''
			if (Array.isArray(translations)) {
				const matched = translations.find(
					item => String(item.value) === String(value) || String(item.key) === String(value),
				)
				return matched ? matched.label || matched.name || matched.valueText || matched.text || '' : ''
			}
			if (typeof translations === 'object') {
				return (
					translations[value] ||
					translations[String(value)] ||
					translations[Number(value)] ||
					translations.label ||
					translations.name ||
					''
				)
			}
			return ''
		},
		formatAxisTime(value) {
			if (!value) return ''
			return this.dayjs(value).format('HH:mm')
		},
		formatTooltip(params) {
			if (!params || !params.length) return ''
			const header = params[0]?.axisValue ? this.formatAxisTime(params[0].axisValue) : '--'
			const lines = params
				.filter(item => item.data !== null && item.data !== undefined)
				.map(item => {
					const value = item.data === null || item.data === undefined ? '--' : Number(item.data).toFixed(2)
					const thresholdText = this.getLegendThresholdText(item.seriesName)
					return `${item.marker}${item.seriesName}：${value} MPa${
						thresholdText ? `<br/>${thresholdText}` : ''
					}`
				})
			return [header, ...lines].join('<br/>')
		},
		getLegendThresholdText(seriesName) {
			const legendItem = (this.chartData.legendItems || []).find(item => item.name === seriesName)
			return legendItem?.thresholdText || ''
		},
		getChartOption() {
			return {
				animation: false,
				title: {
					text: '压力(MPa)',
					left: 0,
					top: 0,
					textStyle: {
						color: '#64748b',
						fontSize: 12,
						fontWeight: 400,
					},
				},
				grid: {
					top: 32,
					right: 24,
					bottom: 32,
					left: 44,
				},
				tooltip: {
					trigger: 'axis',
					backgroundColor: 'rgba(15, 23, 42, 0.92)',
					borderWidth: 0,
					textStyle: {
						color: '#f8fafc',
						fontSize: 12,
					},
					formatter: params => this.formatTooltip(params),
				},
				xAxis: {
					type: 'category',
					boundaryGap: false,
					data: this.chartData.axisTimes,
					axisLine: {
						lineStyle: {
							color: '#e2e8f0',
						},
					},
					axisTick: {
						show: false,
					},
					axisLabel: {
						color: '#64748b',
						fontSize: 12,
						margin: 12,
						formatter: value => this.formatAxisTime(value),
					},
				},
				yAxis: {
					type: 'value',
					min: this.chartData.min,
					max: this.chartData.max,
					axisLine: {
						show: false,
					},
					axisTick: {
						show: false,
					},
					axisLabel: {
						color: '#64748b',
						fontSize: 12,
						margin: 12,
						formatter(value) {
							return Number(value).toFixed(1)
						},
					},
					splitLine: {
						lineStyle: {
							color: '#e2e8f0',
						},
					},
				},
				series: this.chartData.series,
			}
		},
		renderChart() {
			if (!this.chartInstance && this.$refs.chartRef) {
				this.chartInstance = echarts.init(this.$refs.chartRef, null, { renderer: 'canvas' })
			}

			if (this.chartInstance) {
				this.chartInstance.setOption(this.getChartOption(), true)
			}
		},
	},
}
</script>

<style lang="scss" scoped>
.section-card {
	position: relative;
	padding: 22px 24px 18px;
	background: var(--panel-bg);
	border: 1px solid var(--panel-border);
	border-radius: 12px;
	box-shadow: var(--panel-shadow);
	backdrop-filter: blur(12px);
}

.section-head {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 12px;
	margin-bottom: 14px;
}

.section-title {
	margin: 0;
	color: var(--title);
	font-size: 17px;
	font-weight: 700;
	letter-spacing: 0.01em;
}

.chart-legend {
	display: flex;
	align-items: center;
	justify-content: center;
	flex-wrap: wrap;
	gap: 12px;
}

::v-deep .time-range-picker.el-date-editor--date {
	width: 220px !important;
	max-width: 100%;
}

::v-deep .time-range-picker.el-date-editor {
	display: inline-flex;
	align-items: center;
}

::v-deep .time-range-picker .el-range__icon,
::v-deep .time-range-picker .el-range__close-icon,
::v-deep .time-range-picker .el-range-separator {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	height: 100%;
	line-height: 1;
}

::v-deep .time-range-picker .el-range-input {
	height: 100%;
	line-height: 1;
}

.chart-shell {
	position: relative;
	padding-top: 4px;
}

.chart-legend {
	margin-bottom: 8px;
}

.chart-legend__item {
	display: inline-flex;
	align-items: center;
	gap: 8px;
	font-size: 12px;
	color: var(--muted);
}

.chart-legend__dot {
	width: 12px;
	height: 12px;
	border-radius: 3px;
}

.chart-legend__dot--orange {
	background: #f97316;
}

.chart-legend__dot--green {
	background: #16a34a;
}

.chart-canvas {
	width: 100%;
	height: 320px;
}

@media (max-width: 900px) {
	.section-head {
		flex-direction: column;
		align-items: flex-start;
	}

	.chart-canvas {
		height: 280px;
	}
}
</style>
